import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Type, Save, Loader2 } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { toast } from "@/hooks/use-toast";
import { FONT_OPTIONS, ensureFontLoaded } from "./fontOptions";

const GlobalTypographySheet = () => {
  const { settings, updateSettings, loading } = useSiteSettings();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [headingFont, setHeadingFont] = useState("");
  const [bodyFont, setBodyFont] = useState("");
  const [buttonFont, setButtonFont] = useState("");
  const [baseSize, setBaseSize] = useState(16);
  const [baseColor, setBaseColor] = useState("#0A0A14");
  const [lineHeight, setLineHeight] = useState(1.5);

  useEffect(() => {
    if (loading) return;
    setHeadingFont(settings.typography_heading_font || "");
    setBodyFont(settings.typography_body_font || "");
    setButtonFont(settings.typography_button_font || "");
    setBaseSize(parseInt(settings.typography_base_size || "16"));
    setBaseColor(settings.typography_base_color || "#0A0A14");
    setLineHeight(parseFloat(settings.typography_line_height || "1.5"));
  }, [loading, settings, open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      [headingFont, bodyFont, buttonFont].forEach((f) => f && ensureFontLoaded(f));
      await updateSettings([
        { key: "typography_heading_font", value: headingFont, category: "typography" },
        { key: "typography_body_font", value: bodyFont, category: "typography" },
        { key: "typography_button_font", value: buttonFont, category: "typography" },
        { key: "typography_base_size", value: String(baseSize), category: "typography" },
        { key: "typography_base_color", value: baseColor, category: "typography" },
        { key: "typography_line_height", value: String(lineHeight), category: "typography" },
      ]);
      toast({ title: "✅ Typographie globale enregistrée" });
      setOpen(false);
    } catch {
      toast({ title: "Erreur de sauvegarde", variant: "destructive" });
    }
    setSaving(false);
  };

  const FontSelect = ({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) => (
    <div>
      <Label className="text-xs mb-1">{label}</Label>
      <Select value={value || "__default"} onValueChange={(v) => { const next = v === "__default" ? "" : v; ensureFontLoaded(next); onChange(next); }}>
        <SelectTrigger className="h-9"><SelectValue placeholder="Police par défaut" /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="__default">Police par défaut</SelectItem>
          {FONT_OPTIONS.map((f) => (
            <SelectItem key={f.family} value={f.family} style={{ fontFamily: `'${f.family}', sans-serif` }}>
              {f.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button data-editor-ui size="sm" variant="outline" title="Typographie globale">
          <Type className="w-4 h-4 mr-1" />
          Typographie
        </Button>
      </SheetTrigger>
      <SheetContent data-editor-ui className="w-[400px] sm:max-w-md overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
        <SheetHeader>
          <SheetTitle>Typographie globale</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-5">
          <FontSelect value={headingFont} onChange={setHeadingFont} label="Police des titres (h1 → h6)" />
          <FontSelect value={bodyFont} onChange={setBodyFont} label="Police des paragraphes" />
          <FontSelect value={buttonFont} onChange={setButtonFont} label="Police des boutons" />

          <div>
            <Label className="text-xs mb-1">Taille de base : {baseSize}px</Label>
            <Slider value={[baseSize]} min={12} max={22} step={1} onValueChange={([v]) => setBaseSize(v)} />
          </div>

          <div>
            <Label className="text-xs mb-1">Hauteur de ligne globale : {lineHeight.toFixed(2)}</Label>
            <Slider value={[lineHeight * 100]} min={100} max={220} step={5} onValueChange={([v]) => setLineHeight(v / 100)} />
          </div>

          <div>
            <Label className="text-xs mb-1">Couleur de texte globale</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={baseColor} onChange={(e) => setBaseColor(e.target.value)} className="w-10 h-10 rounded border cursor-pointer" />
              <Input value={baseColor} onChange={(e) => setBaseColor(e.target.value)} className="h-10 text-xs font-mono flex-1" />
            </div>
          </div>

          <div className="rounded-lg border p-4 space-y-2" style={{
            fontFamily: bodyFont ? `'${bodyFont}', sans-serif` : undefined,
            fontSize: `${baseSize}px`,
            color: baseColor,
            lineHeight,
          }}>
            <p className="text-xs uppercase text-muted-foreground tracking-wider">Aperçu</p>
            <div style={{ fontFamily: headingFont ? `'${headingFont}', sans-serif` : undefined, fontSize: "1.5em", fontWeight: 700 }}>
              Titre d'exemple
            </div>
            <p>Le renard brun et rapide saute par-dessus le chien paresseux.</p>
            <button
              className="px-3 py-1.5 rounded bg-primary text-primary-foreground"
              style={{ fontFamily: buttonFont ? `'${buttonFont}', sans-serif` : undefined }}
            >
              Bouton d'exemple
            </button>
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Enregistrer les modifications
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default GlobalTypographySheet;
